import { useQuery, UseQueryResult } from "@tanstack/react-query"
import ResponseError from "#/types/responseError"
import { carrotClient } from "#/utils/CarrotClient"
import { InAppMessagingThread, Message } from "#/components/messaging/inAppMessagingTypes"
import { useCurrentUser } from "#/components/context/user/UserContext"

export type CarrotCompanionQueryResult = {
  name: string
  photo?: {
    content_url: string | null
  } | null
  id: number
}

type CarrotCompanion = {
  name: string
  photoUrl?: string | null
  id: number
}

export type HasUnreadMessagesFromCarrotCompanion = {
  hasUnreadMessages: boolean
}

const QUERY_KEY = ["carrot-companion"]

export function useCarrotCompanion(): UseQueryResult<CarrotCompanion, ResponseError> {
  const { isInAppLockdown } = useCurrentUser()

  return useQuery<CarrotCompanionQueryResult, ResponseError, CarrotCompanion>(
    QUERY_KEY,
    () => carrotClient.getCarrotCompanion(),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      select: (data) => ({
        name: data.name,
        photoUrl: data.photo?.content_url ?? null,
        id: data.id
      }),
      enabled: !isInAppLockdown
    }
  )
}

const UNREAD_MESSAGES_QUERY_KEY = ["unread-messages"]

export function useHasUnreadMessagesFromCarrotCompanion(): UseQueryResult<
  HasUnreadMessagesFromCarrotCompanion,
  ResponseError
> {
  return useQuery<HasUnreadMessagesFromCarrotCompanion, ResponseError>(
    UNREAD_MESSAGES_QUERY_KEY,
    () => carrotClient.getHasUnreadMessagesFromCarrotCompanion(),
    {
      refetchOnWindowFocus: false
    }
  )
}

const COMPANION_MESSAGE_QUERY_KEY = ["carrot-companion-message"]

export function useCarrotCompanionCarrotPlanMessage(): UseQueryResult<
  {
    thread: InAppMessagingThread
    carrotPlanMessage: Message
  },
  ResponseError
> {
  return useQuery<
    {
      thread: InAppMessagingThread
      carrotPlanMessage: Message
    },
    ResponseError
  >(COMPANION_MESSAGE_QUERY_KEY, () => carrotClient.getCarrotCompanionCarrotPlanMessage(), {
    refetchOnWindowFocus: false
  })
}
